import { collection, addDoc, doc, setDoc, getDocs, query, where, deleteDoc, orderBy, limit, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Core data interfaces
export interface FormCacheData {
  // Core order data
  orderData: {
    customerNumber: string;
    storeName: string;
    address: string;
    city: string;
    province: string;
    postalCode: string;
    email: string;
    phoneNumber: string;
    poNumber: string;
    orderDate: string;
    deliveryDate: string;
    orderType: string | null;
    specialInstructions: string;
  };
  
  // Form-specific selections - store full product data for accurate names and reporting
  formSelections: {
    heritage: { [productId: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } };
    livingston: { [productId: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } };
    flower: { [productId: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } };
    vegetable: { [productId: string]: { quantity: number; packetDescription?: string; code?: string; price?: number; category?: string } };
  };
  
  // UI state
  uiState: {
    searchTerms: { [formType: string]: string };
    selectedCategories: { [formType: string]: string };
    collapsedSections: { [formType: string]: { [sectionId: string]: boolean } };
  };
  
  // Metadata
  metadata: {
    lastUpdated: Date;
    userId: string;
    sessionId: string;
    formStatus: 'draft' | 'in_progress' | 'completed';
    totalItems: number;
    totalAmount: number;
    version: number;
  };
}

export interface FormStatus {
  status: 'empty' | 'in_progress' | 'completed' | 'error';
  itemCount: number;
  totalAmount: number;
  lastUpdated?: Date;
  hasErrors?: boolean;
}

export interface ProgressInfo {
  totalItems: number;
  totalAmount: number;
  completedForms: number;
  totalForms: number;
  percentage: number;
  lastSaved?: Date;
}

// Cache configuration
const CACHE_CONFIG = {
  localStorage: {
    keyPrefix: 'mckenzie_form_cache_',
    maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days
    maxSize: 5 * 1024 * 1024, // 5MB
  },
  firestore: {
    draftDocumentId: '__formDraftCache',
    maxDrafts: 10,
  },
  autoSave: {
    delay: 2000, // 2 seconds
    maxRetries: 3,
  }
};

export class FormCacheService {
  // Generate cache key for localStorage
  static generateCacheKey(userId: string, formType?: string): string {
    const baseKey = `${CACHE_CONFIG.localStorage.keyPrefix}${userId}`;
    return formType ? `${baseKey}_${formType}` : baseKey;
  }

  // Check if data is stale
  static isDataStale(data: FormCacheData, maxAge: number = CACHE_CONFIG.localStorage.maxAge): boolean {
    const now = new Date().getTime();
    const dataTime = new Date(data.metadata.lastUpdated).getTime();
    return (now - dataTime) > maxAge;
  }

  // Merge cache data intelligently
  static mergeCacheData(existing: FormCacheData, newData: Partial<FormCacheData>): FormCacheData {
    return {
      orderData: { ...existing.orderData, ...newData.orderData },
      formSelections: {
        heritage: { ...existing.formSelections.heritage, ...newData.formSelections?.heritage },
        livingston: { ...existing.formSelections.livingston, ...newData.formSelections?.livingston },
        flower: { ...existing.formSelections.flower, ...newData.formSelections?.flower },
        vegetable: { ...existing.formSelections.vegetable, ...newData.formSelections?.vegetable },
      },
      uiState: {
        searchTerms: { ...existing.uiState.searchTerms, ...newData.uiState?.searchTerms },
        selectedCategories: { ...existing.uiState.selectedCategories, ...newData.uiState?.selectedCategories },
        collapsedSections: { ...existing.uiState.collapsedSections, ...newData.uiState?.collapsedSections },
      },
      metadata: {
        ...existing.metadata,
        ...newData.metadata,
        lastUpdated: new Date(),
        version: existing.metadata.version + 1,
      },
    };
  }

  // Calculate form status
  static calculateFormStatus(formType: keyof FormCacheData['formSelections'], selections: any): FormStatus {
    // Calculate actual item count by summing quantities
    const itemCount = Object.values(selections).reduce((sum: number, item: any) => {
      return sum + (typeof item === 'number' ? item : item.quantity);
    }, 0);
    
    // Debug logging
    console.log(`FormCacheService - calculateFormStatus - formType: ${formType}, selections:`, selections);
    console.log(`FormCacheService - calculateFormStatus - itemCount: ${itemCount}`);
    
    const totalAmount = Object.entries(selections).reduce((total, [productId, item]: [string, any]) => {
      const quantity = typeof item === 'number' ? item : item.quantity;
      const price = typeof item === 'number' ? 0 : (item.price || 0);
      return total + (quantity * price);
    }, 0);

    return {
      status: itemCount > 0 ? 'in_progress' : 'empty',
      itemCount,
      totalAmount,
    };
  }

  // Calculate overall progress
  static calculateProgress(cacheData: FormCacheData): ProgressInfo {
    const allSelections = [
      ...Object.values(cacheData.formSelections.heritage),
      ...Object.values(cacheData.formSelections.livingston),
      ...Object.values(cacheData.formSelections.flower),
      ...Object.values(cacheData.formSelections.vegetable),
    ];

    const totalItems = allSelections.reduce((sum: number, item: any) => {
      return sum + (typeof item === 'number' ? item : item.quantity);
    }, 0);
    
    const totalAmount = allSelections.reduce((sum: number, item: any) => {
      const quantity = typeof item === 'number' ? item : item.quantity;
      const price = typeof item === 'number' ? 0 : (item.price || 0);
      return sum + (quantity * price);
    }, 0);
    
    const formTypes = ['heritage', 'livingston', 'flower', 'vegetable'] as const;
    const completedForms = formTypes.filter(formType => {
      const selections = cacheData.formSelections[formType];
      return Object.values(selections).some(item => {
        const qty = typeof item === 'number' ? item : item.quantity;
        return qty > 0;
      });
    }).length;

    return {
      totalItems,
      totalAmount,
      completedForms,
      totalForms: formTypes.length,
      percentage: Math.round((completedForms / formTypes.length) * 100),
      lastSaved: cacheData.metadata.lastUpdated,
    };
  }

  // localStorage methods
  static saveToLocalStorage(key: string, data: FormCacheData): void {
    try {
      const serialized = JSON.stringify(data);
      
      // Check size limit
      if (serialized.length > CACHE_CONFIG.localStorage.maxSize) {
        console.warn('Cache data too large for localStorage, skipping save');
        return;
      }

      localStorage.setItem(key, serialized);
      console.log(`📦 Cache saved to localStorage: ${key}`);
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  static loadFromLocalStorage(key: string): FormCacheData | null {
    try {
      const serialized = localStorage.getItem(key);
      if (!serialized) return null;

      const data = JSON.parse(serialized) as FormCacheData;
      
      // Check if data is stale
      if (this.isDataStale(data)) {
        console.log('Cache data is stale, removing from localStorage');
        this.clearLocalStorage(key);
        return null;
      }

      console.log(`📦 Cache loaded from localStorage: ${key}`);
      return data;
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
      return null;
    }
  }

  static clearLocalStorage(key: string): void {
    try {
      localStorage.removeItem(key);
      console.log(`📦 Cache cleared from localStorage: ${key}`);
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }

  // Firebase Firestore methods
  static async saveDraftToFirestore(userId: string, data: FormCacheData): Promise<void> {
    try {
      const draftRef = this.getDraftDocRef(userId);
      const draftData = {
        ...data,
        metadata: {
          ...data.metadata,
          lastUpdated: new Date(),
        },
      };

      await setDoc(draftRef, draftData, { merge: true });
      console.log(`☁️ Draft saved to Firestore: ${userId}`);
    } catch (error) {
      console.error('Failed to save draft to Firestore:', error);
      throw error;
    }
  }

  static async loadDraftFromFirestore(userId: string): Promise<FormCacheData | null> {
    try {
      const draftRef = this.getDraftDocRef(userId);
      const draftSnap = await getDoc(draftRef);
      
      if (!draftSnap.exists()) {
        console.log('No draft found in Firestore');
        return null;
      }

      const draftData = draftSnap.data() as FormCacheData;
      console.log(`☁️ Draft loaded from Firestore: ${userId}`);
      return draftData;
    } catch (error) {
      console.error('Failed to load draft from Firestore:', error);
      return null;
    }
  }

  static async deleteDraftFromFirestore(userId: string): Promise<void> {
    try {
      const draftRef = this.getDraftDocRef(userId);
      await deleteDoc(draftRef);
      console.log(`☁️ Draft deleted from Firestore: ${userId}`);
    } catch (error) {
      console.error('Failed to delete draft from Firestore:', error);
      throw error;
    }
  }

  // Cleanup methods
  static cleanupOldCache(): void {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(CACHE_CONFIG.localStorage.keyPrefix));
      
      cacheKeys.forEach(key => {
        const data = this.loadFromLocalStorage(key);
        if (!data || this.isDataStale(data)) {
          this.clearLocalStorage(key);
        }
      });
      
      console.log('🧹 Cache cleanup completed');
    } catch (error) {
      console.error('Failed to cleanup cache:', error);
    }
  }

  // Validation methods
  static validateCacheData(data: any): data is FormCacheData {
    return (
      data &&
      typeof data === 'object' &&
      data.orderData &&
      data.formSelections &&
      data.uiState &&
      data.metadata &&
      typeof data.metadata.userId === 'string' &&
      typeof data.metadata.sessionId === 'string'
    );
  }

  // Utility methods
  static getStorageInfo(): { used: number; available: number; percentage: number } {
    try {
      let used = 0;
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          used += localStorage[key].length;
        }
      }
      
      const available = CACHE_CONFIG.localStorage.maxSize - used;
      const percentage = Math.round((used / CACHE_CONFIG.localStorage.maxSize) * 100);
      
      return { used, available, percentage };
    } catch (error) {
      console.error('Failed to get storage info:', error);
      return { used: 0, available: 0, percentage: 0 };
    }
  }

  private static getDraftDocRef(storeId: string) {
    if (!storeId) {
      throw new Error('Store ID is required to resolve draft document path');
    }

    return doc(
      db,
      'Stores',
      storeId,
      'StoreOrders',
      CACHE_CONFIG.firestore.draftDocumentId
    );
  }
}
